/**
 * Step 2 — Rent Roll Submission
 *
 * Required for ALL audits. PDF or Excel.
 *
 * Flow:
 *   1. User uploads file → file POST → server stores it
 *   2. Server tries to parse it. We display a Preview card with detected values.
 *   3. If parse FAILS or detects 0 units, the card still renders with an
 *      empty summary so the user can manually enter values and advance.
 *      Earlier version returned null on parse failure — that left the user
 *      with no Confirm button (Matt hit this bug).
 *   4. User edits / confirms values → onNext.
 */
import React, { useState } from 'react';
import { api } from '../../../api';
import ParsedPreviewCard from './ParsedPreviewCard';

const BRAND = '#3F66A0';
const BRAND_TINT = '#eef3fb';

const cardStyle = {
  background: '#fff',
  borderRadius: 16,
  padding: '28px 32px',
  marginBottom: 20,
  border: '1px solid #e2e8f0',
};

const sectionTitleStyle = {
  margin: 0,
  marginBottom: 4,
  fontSize: 17,
  fontWeight: 700,
  color: '#0f172a',
  letterSpacing: '-0.01em',
};

const sectionHelpStyle = {
  marginTop: 0,
  marginBottom: 22,
  fontSize: 13,
  color: '#64748b',
  lineHeight: 1.5,
};

const EMPTY_SUMMARY = {
  total_units: 0,
  occupied_units: 0,
  vacant_units: 0,
  model_units: 0,
  ntv_units: 0,
  future_residents: 0,
};

export default function Step2RentRoll({ draftId, formData, onChange, onNext, onBack, files, refreshFiles }) {
  const [uploading, setUploading] = useState(false);
  const [parsing, setParsing] = useState(false);
  const [error, setError] = useState('');
  const [preview, setPreview] = useState(formData?.rent_roll_summary || null);
  const [parseFailed, setParseFailed] = useState(false);

  const existing = (files || []).filter(f => f.file_kind === 'rent_roll');
  const currentFile = existing[0];

  const handleUpload = async (e) => {
    const fl = e.target.files;
    if (!fl || !fl.length) return;
    setUploading(true);
    setError('');
    setParseFailed(false);
    try {
      // Replace any existing rent_roll file
      for (const f of existing) {
        await api.deleteAuditFile(draftId, f.id);
      }
      await api.uploadAuditFiles(draftId, 'rent_roll', Array.from(fl));
      await refreshFiles();
      setUploading(false);
      setParsing(true);
      try {
        const res = await api.parseAuditRentRoll(draftId);
        if (res?.summary && res.summary.total_units > 0) {
          setPreview(res.summary);
          onChange({ ...formData, rent_roll_summary: res.summary });
        } else {
          // Parse returned nothing usable — fall back to manual entry
          setPreview({ ...EMPTY_SUMMARY });
          setParseFailed(true);
          setError("We couldn't detect units in this file. Enter values manually below.");
        }
      } catch (parseErr) {
        // Parse threw — file uploaded fine, just couldn't read it
        setPreview({ ...EMPTY_SUMMARY });
        setParseFailed(true);
        setError(`Couldn't auto-read this file (${parseErr.message}). Enter values manually below.`);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setUploading(false);
      setParsing(false);
    }
  };

  const handleEdit = (newSummary) => {
    setPreview(newSummary);
    onChange({ ...formData, rent_roll_summary: newSummary });
  };

  const handleReupload = () => {
    setPreview(null);
    setParseFailed(false);
    setError('');
  };

  // Manual-entry escape hatch — even before any upload, let the user skip
  // the file altogether and type values in. Useful if the rent roll is
  // already in the operator's head or on a printed sheet.
  const startManualEntry = () => {
    setPreview({ ...EMPTY_SUMMARY });
    setParseFailed(true);
  };

  const canConfirm = preview && (preview.total_units > 0 || parseFailed);

  return (
    <div className="audit-step audit-step-2">

      {/* ─── Card 1: Upload ─── */}
      <section style={cardStyle}>
        <h2 style={sectionTitleStyle}>Rent Roll</h2>
        <p style={sectionHelpStyle}>
          Upload the property's rent roll as a PDF or Excel file. We'll auto-detect
          unit counts and occupancy. If our parser can't read your file, you'll be able
          to enter the values manually.
        </p>

        {!preview && (
          <>
            <label style={{
              display: 'block', padding: '32px 24px', borderRadius: 14,
              border: `2px dashed ${uploading || parsing ? BRAND : '#cbd5e1'}`,
              background: uploading || parsing ? BRAND_TINT : '#fafbfc',
              textAlign: 'center', cursor: uploading || parsing ? 'wait' : 'pointer',
              transition: 'all 0.15s',
            }}>
              <input
                type="file"
                accept=".pdf,.xlsx,.xls"
                onChange={handleUpload}
                disabled={uploading || parsing}
                style={{ display: 'none' }}
              />
              <div style={{ fontSize: 36, marginBottom: 8 }}>📄</div>
              <div style={{ fontSize: 15, fontWeight: 700, color: '#0f172a' }}>
                {uploading ? 'Uploading…' :
                  parsing ? 'Reading rent roll…' :
                    currentFile ? `Re-upload (current: ${currentFile.original_filename})` :
                      'Click to upload PDF or Excel'}
              </div>
              <div style={{ fontSize: 12, color: '#64748b', marginTop: 4 }}>
                .pdf, .xlsx, or .xls
              </div>
            </label>

            <div style={{ marginTop: 14, textAlign: 'center' }}>
              <button
                type="button"
                onClick={startManualEntry}
                style={{
                  padding: 0, background: 'none', border: 'none',
                  color: BRAND, fontSize: 13, fontWeight: 600, cursor: 'pointer',
                }}
              >
                Don't have a file? Enter values manually →
              </button>
            </div>
          </>
        )}

        {error && (
          <div style={{
            marginTop: preview ? 0 : 16,
            marginBottom: preview ? 16 : 0,
            padding: '12px 16px', borderRadius: 10,
            background: '#fef3c7', border: '1px solid #fcd34d',
            color: '#78350f', fontSize: 13, fontWeight: 600,
          }}>
            {error}
          </div>
        )}
      </section>

      {/* ─── Card 2: Preview / Manual entry ─── */}
      {preview && (
        <section style={cardStyle}>
          <ParsedPreviewCard
            summary={preview}
            kind="rent_roll"
            startInEdit={parseFailed}
            onEdit={handleEdit}
            onConfirm={onNext}
            onReupload={handleReupload}
            confirmDisabled={!canConfirm}
          />
        </section>
      )}

      {/* Step actions */}
      <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 8 }}>
        <button
          type="button"
          onClick={onBack}
          style={{
            padding: '14px 24px', borderRadius: 12, fontSize: 14, fontWeight: 700,
            border: '1.5px solid #e2e8f0', cursor: 'pointer',
            background: '#fff', color: '#475569',
          }}
        >
          ← Back
        </button>
        {preview && (
          <button
            type="button"
            onClick={onNext}
            disabled={!canConfirm}
            style={{
              padding: '14px 28px', borderRadius: 12, fontSize: 15, fontWeight: 700,
              border: 'none', cursor: canConfirm ? 'pointer' : 'not-allowed',
              background: canConfirm ? BRAND : '#cbd5e1',
              color: '#fff', transition: 'all 0.15s',
              boxShadow: canConfirm ? '0 2px 6px rgba(63,102,160,0.25)' : 'none',
            }}
          >
            Next: Vehicles →
          </button>
        )}
      </div>
    </div>
  );
}
